import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, CalendarDays, Check, Clock, Plus, Trash2 } from 'lucide-react'
import { AxiosError } from 'axios'

import api from '@/services/api'

/**
 * The assignment SLA policy, editable by the desk (register F1 / F2).
 *
 * `app_global_settings["sla"]` has driven the engine since it was written —
 * threshold, escalation window, business hours, timezone, holiday calendar and
 * per-stage overrides — and no route ever wrote it. `PATCH /settings/platform`
 * drops keys absent from `DEFAULT_PLATFORM`, so changing any of it meant a
 * direct database write, which in practice meant only Helios could. For a
 * policy the desk is measured against, that is the wrong owner.
 *
 * Two things this screen refuses to do:
 *
 *   * **Invent a saved state.** Every field renders what the server read back
 *     through the same loader the nightly sweep uses, so the screen cannot show
 *     a policy the engine would not apply.
 *   * **Swallow a rejection.** The API validates harder than the sweep — the
 *     sweep clamps junk to defaults so a bad value cannot take the run down,
 *     which would otherwise let an administrator save 900 days and be served 2.
 *     A refusal is shown verbatim rather than reverting the field silently.
 */

type SlaSettings = {
  timezone: string
  businessStartHour: number
  businessEndHour: number
  thresholdDays: number
  escalationDays: number
  holidays: string[]
  useDefaultBankHolidays: boolean
  stageThresholds: Record<string, number>
  stages: string[]
  defaults: {
    timezone: string
    businessStartHour: number
    businessEndHour: number
    thresholdDays: number
    escalationDays: number
  }
  defaultBankHolidays: string[]
  defaultBankHolidayYear: number
}

type Patch = Partial<
  Pick<
    SlaSettings,
    | 'timezone'
    | 'businessStartHour'
    | 'businessEndHour'
    | 'thresholdDays'
    | 'escalationDays'
    | 'holidays'
    | 'useDefaultBankHolidays'
    | 'stageThresholds'
  >
>

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="font-mono text-[9px] uppercase tracking-widest text-stone-500">{label}</span>
      <span className="mt-1 block">{children}</span>
      {hint && <span className="mt-0.5 block text-[9px] leading-tight text-stone-600">{hint}</span>}
    </label>
  )
}

const INPUT =
  'w-full rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 focus:border-amber-300/40 focus:outline-none'

export default function SlaSettingsTab() {
  const [settings, setSettings] = useState<SlaSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)
  const [newHoliday, setNewHoliday] = useState('')
  /**
   * Bumped whenever the server has spoken. The numeric inputs are uncontrolled
   * (`defaultValue`) so typing stays responsive, and React will not rewrite a
   * DOM value on re-render — so after a REFUSED change the field kept showing
   * the rejected number while the server still held the old one. Keying on this
   * remounts them, which is the only way an uncontrolled input goes back to
   * stored truth when that truth did not itself change.
   */
  const [revision, setRevision] = useState(0)

  /**
   * `silent` is for the re-read after a refusal: it must not clear the error it
   * is being run underneath, and it must not swap the whole form for a spinner
   * when the administrator is mid-edit. Without it the refusal was set and then
   * wiped a moment later by this very function, so a rejected value snapped
   * back with nothing on screen saying why — which is the failure this screen
   * exists to avoid.
   */
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const res = await api.get<SlaSettings>('/settings/sla')
      setSettings(res.data)
      setRevision((r) => r + 1)
      if (!silent) setError(null)
    } catch (e) {
      setError(
        e instanceof AxiosError && e.response?.status === 403
          ? 'You do not have permission to view the SLA policy.'
          : 'The SLA policy could not be read.',
      )
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const save = async (patch: Patch) => {
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      const res = await api.patch<SlaSettings>('/settings/sla', patch)
      setSettings(res.data)
      setRevision((r) => r + 1)
      setSaved(true)
      window.setTimeout(() => setSaved(false), 2500)
    } catch (e) {
      // The server's own words. A refusal reworded here would hide which bound
      // was crossed, and the bounds are the point of the validation.
      const detail =
        e instanceof AxiosError ? (e.response?.data as { detail?: string } | undefined)?.detail : undefined
      // Re-read FIRST so the fields show what is actually stored rather than
      // the rejected value left sitting in the input, then state the refusal —
      // in the other order the reload wipes the message it was meant to explain.
      await load(true)
      setError(detail || 'The change could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return <p className="text-[11px] text-stone-500" aria-busy="true">Reading the SLA policy…</p>
  }
  if (!settings) {
    return <p className="text-[11px] text-red-300">{error ?? 'The SLA policy could not be read.'}</p>
  }

  const d = settings.defaults

  return (
    <div className="space-y-4">
      <header>
        <h2 className="flex items-center gap-1.5 text-[13px] font-semibold text-stone-100">
          <Clock size={13} className="text-amber-300" /> Assignment SLA
        </h2>
        <p className="mt-0.5 text-[10px] leading-relaxed text-stone-500">
          How long a deal may sit with the person it was assigned to before the desk is told.
          Counted in business hours against the calendar below — a deal handed over at 6pm Friday
          starts its clock Monday morning. Separate from stage aging, which measures how long a
          deal has been in a stage rather than with a person.
        </p>
      </header>

      {error && (
        <p className="flex items-start gap-1.5 rounded border border-red-400/25 bg-red-400/[0.06] px-2.5 py-1.5 text-[11px] text-red-300">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          {error}
        </p>
      )}
      {saved && !error && (
        <p className="flex items-center gap-1.5 text-[11px] text-emerald-400">
          <Check size={12} /> Saved
        </p>
      )}

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Threshold" hint={`Business days before the owner is told. Default ${d.thresholdDays}.`}>
          <input
            key={`threshold-${revision}`}
            type="number"
            min={0}
            max={365}
            aria-label="Threshold days"
            className={INPUT}
            defaultValue={settings.thresholdDays}
            disabled={busy}
            onBlur={(e) => {
              const value = Number(e.target.value)
              if (Number.isInteger(value) && value !== settings.thresholdDays) {
                void save({ thresholdDays: value })
              }
            }}
          />
        </Field>
        <Field label="Escalation" hint={`Further business days before escalation. Default ${d.escalationDays}.`}>
          <input
            key={`escalation-${revision}`}
            type="number"
            min={0}
            max={365}
            aria-label="Escalation days"
            className={INPUT}
            defaultValue={settings.escalationDays}
            disabled={busy}
            onBlur={(e) => {
              const value = Number(e.target.value)
              if (Number.isInteger(value) && value !== settings.escalationDays) {
                void save({ escalationDays: value })
              }
            }}
          />
        </Field>
        <Field label="Day starts" hint={`Local hour, 0–23. Default ${d.businessStartHour}.`}>
          <input
            key={`start-${revision}`}
            type="number"
            min={0}
            max={23}
            aria-label="Business start hour"
            className={INPUT}
            defaultValue={settings.businessStartHour}
            disabled={busy}
            onBlur={(e) => {
              const value = Number(e.target.value)
              if (Number.isInteger(value) && value !== settings.businessStartHour) {
                void save({ businessStartHour: value })
              }
            }}
          />
        </Field>
        <Field label="Day ends" hint={`Local hour, 1–24. Default ${d.businessEndHour}.`}>
          <input
            key={`end-${revision}`}
            type="number"
            min={1}
            max={24}
            aria-label="Business end hour"
            className={INPUT}
            defaultValue={settings.businessEndHour}
            disabled={busy}
            onBlur={(e) => {
              const value = Number(e.target.value)
              if (Number.isInteger(value) && value !== settings.businessEndHour) {
                void save({ businessEndHour: value })
              }
            }}
          />
        </Field>
      </section>

      <Field label="Timezone" hint={`IANA name. The clock and the working day are local to this. Default ${d.timezone}.`}>
        <input
          key={`tz-${revision}`}
          type="text"
          aria-label="Timezone"
          className={`${INPUT} max-w-xs`}
          defaultValue={settings.timezone}
          disabled={busy}
          onBlur={(e) => {
            const value = e.target.value.trim()
            if (value && value !== settings.timezone) void save({ timezone: value })
          }}
        />
      </Field>

      <section>
        <h3 className="font-mono text-[9px] uppercase tracking-widest text-stone-500">
          Per-stage overrides
        </h3>
        <p className="mb-1.5 text-[9px] leading-tight text-stone-600">
          Blank uses the threshold above. A stage that legitimately dwells — Committee, Closing —
          is why this exists.
        </p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {settings.stages.map((stage) => (
            <label key={stage} className="block">
              <span className="block truncate text-[10px] text-stone-400" title={stage}>
                {stage}
              </span>
              <input
                key={`${stage}-${revision}`}
                type="number"
                min={0}
                max={365}
                aria-label={`${stage} threshold`}
                placeholder={String(settings.thresholdDays)}
                className={`${INPUT} mt-0.5`}
                defaultValue={settings.stageThresholds[stage] ?? ''}
                disabled={busy}
                onBlur={(e) => {
                  const raw = e.target.value.trim()
                  const next = { ...settings.stageThresholds }
                  if (raw === '') {
                    if (!(stage in next)) return
                    delete next[stage]
                  } else {
                    const value = Number(raw)
                    if (!Number.isInteger(value) || next[stage] === value) return
                    next[stage] = value
                  }
                  void save({ stageThresholds: next })
                }}
              />
            </label>
          ))}
        </div>
      </section>

      <section>
        <h3 className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-stone-500">
          <CalendarDays size={11} /> Closed dates
        </h3>
        <label className="mt-1 flex items-start gap-2 text-[11px] text-stone-300">
          <input
            type="checkbox"
            checked={settings.useDefaultBankHolidays}
            aria-label="Use the standard bank holiday calendar"
            disabled={busy}
            onChange={(e) => void save({ useDefaultBankHolidays: e.target.checked })}
            className="mt-0.5 h-3 w-3 shrink-0 accent-amber-300"
          />
          <span>
            Use the standard bank holiday calendar
            <span className="block text-[9px] leading-tight text-stone-600">
              {settings.defaultBankHolidays.length} dates in {settings.defaultBankHolidayYear}. Off
              means the desk observes only the dates listed below — nothing is added back.
            </span>
          </span>
        </label>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {settings.holidays.length === 0 && (
            <span className="text-[10px] text-stone-600">No additional closed dates.</span>
          )}
          {settings.holidays.map((day) => (
            <span
              key={day}
              className="inline-flex items-center gap-1 rounded border border-stone-700 px-1.5 py-0.5 text-[10px] text-stone-300"
            >
              {day}
              <button
                type="button"
                aria-label={`Remove ${day}`}
                disabled={busy}
                onClick={() => void save({ holidays: settings.holidays.filter((h) => h !== day) })}
                className="text-stone-500 hover:text-red-300"
              >
                <Trash2 size={10} />
              </button>
            </span>
          ))}
        </div>

        <div className="mt-2 flex items-center gap-1.5">
          <input
            type="date"
            aria-label="New closed date"
            className={`${INPUT} max-w-[10rem]`}
            value={newHoliday}
            disabled={busy}
            onChange={(e) => setNewHoliday(e.target.value)}
          />
          <button
            type="button"
            disabled={busy || !newHoliday || settings.holidays.includes(newHoliday)}
            onClick={() => {
              void save({ holidays: [...settings.holidays, newHoliday] })
              setNewHoliday('')
            }}
            className="inline-flex items-center gap-1 rounded border border-amber-300/40 bg-amber-300/[0.1] px-2 py-1 text-[11px] text-amber-100 disabled:opacity-40"
          >
            <Plus size={11} /> Add
          </button>
        </div>
      </section>
    </div>
  )
}
